import {useQuery} from '@tanstack/react-query';

import config from '../../config';
import {AirtableHomepageItem, HomepageCarousel, isCarouselItem} from '../../models/airtable';
import apiService from '../../transport/apiService';
import {logger} from '../../utils/logger';

import {isValidContentKey, UseHomepageParams, UseHomepageResult} from './types';

export const useHomepage = ({storeNumber, storeState, page}: UseHomepageParams) => {
  const resolvedStoreNumber = storeNumber ?? config.DEFAULT_STORE;
  const resolvedStoreState = (storeState ?? config.DEFAULT_STORE_STATE)?.toUpperCase();

  return useQuery<UseHomepageResult>({
    queryKey: ['homepage', resolvedStoreNumber, resolvedStoreState, page],

    queryFn: async () => {
      const response = await apiService.getHomepage(
        resolvedStoreNumber!,
        resolvedStoreState!,
        page,
      );

      if (!response || !Array.isArray(response)) {
        throw new Error('Homepage response missing or invalid');
      }

      const data: Record<string, AirtableHomepageItem> = {};
      const unknownData: Record<string, AirtableHomepageItem> = {};
      let carousel: HomepageCarousel | undefined;

      const carouselItems: AirtableHomepageItem[] = [];

      for (const item of response) {
        if (isCarouselItem(item)) {
          carouselItems.push(item);
          continue;
        }

        const key = item.content;
        if (isValidContentKey(key)) {
          data[key] = item;
        } else {
          logger.info('useHomepage', 'Unknown content key', {key});
          unknownData[key] = item;
        }
      }

      if (carouselItems.length > 0) {
        carousel = {
          slides: carouselItems.map(item => ({
            content: item.content,
            heroImage: item.heroImage!,
            heroImageWeb: item.heroImageWeb!,
            pageLink: item.pageLink,
            platform: item.platform,
            brands: item.brands,
            brandsUrl: item.brandsUrl,
          })),
        };
      }

      return {data, carousel, unknownData};
    },

    enabled: !!resolvedStoreNumber && !!resolvedStoreState,
  });
};
